import { useEffect, useRef, useState } from 'react'
import type { Overlay, OutputSpec } from '@shared/types'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import {
  endCardHitBox,
  freeTextHitBox,
  labelHitBox,
  LABEL_SLOTS,
  logoHitBox,
  nearestLabelSlot,
  nearestTitleSlot,
  resolveFreeTextPosition,
  TITLE_SLOTS,
  titleHitBox,
  type HitBox,
  type LabelSlot,
  type TitleSlot,
} from '../../../lib/previewMath'
import {
  EndCardFallback,
  FreeTextFallback,
  LabelFallback,
  LogoFallback,
  TitleCardFallback,
} from './overlayFallback'

const RENDER_CACHE = new Map<string, string | 'not_implemented' | 'error'>()

function overlayKey(o: Overlay, output: OutputSpec, hideEmblem: boolean) {
  return `${JSON.stringify(o)}::${output.width}x${output.height}::hideEmblem=${hideEmblem}`
}

/** One overlay: server PNG (100%-frame, absolutely positioned) or CSS/HTML fallback. */
function OverlayItem({
  overlay,
  output,
  hideEmblem,
}: {
  overlay: Overlay
  output: OutputSpec
  hideEmblem: boolean
}) {
  const key = overlayKey(overlay, output, hideEmblem)
  const [state, setState] = useState<string | 'not_implemented' | 'error' | 'loading'>(
    RENDER_CACHE.get(key) ?? 'loading',
  )
  const requested = useRef(false)
  // reviewer 差し戻し M1: position 変更等で `key` が変わるたびに `state` は
  // 一旦 'loading' へ戻る（新しい key はまだ RENDER_CACHE に無いことが多い）。
  // その間そのまま usePng=false でフォールバックへ切り替えると、ドロップの
  // たび新 PNG が届くまで見た目が別物（CSS 近似）へ跳ねる。直前に表示していた
  // 実在の PNG URL をここに保持し、loading 中はそれを stale-while-revalidate
  // として出し続ける（新 PNG が届き次第、下の usePng 分岐で自然に差し替わる）。
  const lastUrlRef = useRef<string | null>(null)

  useEffect(() => {
    setState(RENDER_CACHE.get(key) ?? 'loading')
    requested.current = false
  }, [key])

  useEffect(() => {
    if (state !== 'loading' || requested.current) return
    requested.current = true
    let cancelled = false
    api.renderOverlay(overlay, output, { hideEmblem }).then((res) => {
      if (cancelled) return
      if (res.ok) {
        RENDER_CACHE.set(key, res.url)
        setState(res.url)
      } else {
        RENDER_CACHE.set(key, res.reason)
        setState(res.reason)
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, key])

  const isFreshUrl = state !== 'loading' && state !== 'not_implemented' && state !== 'error'

  useEffect(() => {
    if (isFreshUrl) lastUrlRef.current = state as string
  }, [isFreshUrl, state])

  const usePng = isFreshUrl || (state === 'loading' && lastUrlRef.current != null)
  const displayUrl = isFreshUrl ? (state as string) : lastUrlRef.current

  if (usePng && displayUrl) {
    return (
      <img
        src={displayUrl}
        alt=""
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        data-testid="overlay-png"
        data-overlay-kind={overlay.kind}
        data-stale={isFreshUrl ? undefined : 'true'}
      />
    )
  }

  // fallback: CSS/HTML approximation (also used transiently while 'loading')
  return (
    <div
      className="absolute inset-0"
      data-testid="overlay-fallback"
      data-overlay-kind={overlay.kind}
    >
      {overlay.kind === 'title' && <TitleCardFallback overlay={overlay} />}
      {overlay.kind === 'label' && <LabelFallback overlay={overlay} />}
      {overlay.kind === 'end' && <EndCardFallback overlay={overlay} />}
      {overlay.kind === 'logo' && <LogoFallback overlay={overlay} />}
      {overlay.kind === 'free' && <FreeTextFallback overlay={overlay} />}
    </div>
  )
}

/** UI MK2 第2段 2a-2/2a-5: ドラッグ中の状態。`mode:'free'` は FreeText の自由ドラッグ
 *  （x/y 0..1）、`mode:'slot'` は Title/Label のスロット吸着ドラッグ。ここでの位置更新は
 *  ローカル state のみ（`deltaPx` を translate に使う）— store への反映は pointerup で
 *  1 回だけ（task spec §2-1/§2-2）。 */
interface DragState {
  overlayId: string
  mode: 'free' | 'slot'
  slotKind?: 'title' | 'label'
  startClientX: number
  startClientY: number
  /** free のみ: ドラッグ開始時点の overlay.x/y */
  startX?: number
  startY?: number
  deltaPx: { x: number; y: number }
  nearestSlot?: TitleSlot | LabelSlot
}

function hitBoxFor(o: Overlay, output: OutputSpec): HitBox | null {
  switch (o.kind) {
    case 'title':
      return titleHitBox(o.position)
    case 'label':
      return labelHitBox(o.position)
    case 'free':
      return freeTextHitBox(o, output)
    case 'logo':
      return logoHitBox(output)
    case 'end':
      return endCardHitBox()
    default:
      return null
  }
}

/** task spec §2-2: ドラッグ中のスロット候補をゴースト枠で表示し、最も近いものをハイライト。 */
function SlotGhosts({ slotKind, nearest }: { slotKind: 'title' | 'label'; nearest?: TitleSlot | LabelSlot }) {
  const keys: string[] = slotKind === 'title' ? TITLE_SLOTS : LABEL_SLOTS
  return (
    <>
      {keys.map((key) => {
        const box = slotKind === 'title' ? titleHitBox(key as TitleSlot) : labelHitBox(key as LabelSlot)
        const isNearest = key === nearest
        return (
          <div
            key={key}
            data-testid="overlay-slot-ghost"
            data-slot={key}
            data-nearest={isNearest}
            className={`absolute rounded border-2 border-dashed pointer-events-none transition-colors ${
              isNearest ? 'border-accent-gold bg-accent-gold/10' : 'border-white/25'
            }`}
            style={{
              left: `${box.leftNorm * 100}%`,
              top: `${box.topNorm * 100}%`,
              width: `${box.widthNorm * 100}%`,
              height: `${box.heightNorm * 100}%`,
            }}
          />
        )
      })}
    </>
  )
}

export function OverlayLayer({ overlays, output }: { overlays: Overlay[]; output: OutputSpec }) {
  // REVIEW_v1.md M4: a title/end card must not draw its own venue emblem
  // when this Composition also has a persistent Logo overlay on screen —
  // `overlays` here is `activeOverlaysAt(...)` (lib/timelineMath.ts), which
  // always includes the Logo overlay once present (it has no
  // startSec/durationSec window), so this check needs nothing beyond the
  // list already passed in. Mirrors render/index.ts's server-side decision
  // (resolveOverlayPngPath's `hideEmblem`) so the preview matches the export.
  const hasLogo = overlays.some((o) => o.kind === 'logo')

  const selectedIds = useEditorStore((s) => s.selectedIds)
  const select = useEditorStore((s) => s.select)
  const toggleSelection = useEditorStore((s) => s.toggleSelection)
  const updateOverlay = useEditorStore((s) => s.updateOverlay)
  const beginHistoryBatch = useEditorStore((s) => s.beginHistoryBatch)
  const endHistoryBatch = useEditorStore((s) => s.endHistoryBatch)

  const rootRef = useRef<HTMLDivElement>(null)
  // reviewer 差し戻し B1: ドラッグ状態の正本は ref（`dragRef`）に持つ。`drag`
  // (useState) は描画（translate・ゴースト表示）専用のミラーで、副作用
  // （updateOverlay/endHistoryBatch）は絶対に setState の functional updater
  // の中に書かない — StrictMode は開発時に updater 関数を意図的に2回呼ぶため
  // （純粋関数であることの検証）、updater 内で副作用を実行すると1ドラッグに
  // つき2回実行されてしまう（Undo が1エントリで戻らない実バグの原因だった）。
  const dragRef = useRef<DragState | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)

  function isSelected(id: string) {
    return selectedIds.some((s) => s.kind === 'overlay' && s.id === id)
  }

  return (
    // task spec §2 (UI_MK2_PHASE2.md 2a): このラッパー自体は `pointer-events-none`
    // — フレーム全体を覆う絶対配置の子要素（PNG/フォールバック）が下の
    // 映像クリックを飲み込まないよう、実際に操作を受けるのは各オーバーレイの
    // 「当たり判定」矩形（`overlay-hit`）だけに限定する。
    <div ref={rootRef} className="absolute inset-0 pointer-events-none" data-testid="overlay-interaction-root">
      {overlays.map((o) => {
        const dragging = drag?.overlayId === o.id
        const style: React.CSSProperties = dragging
          ? { transform: `translate(${drag!.deltaPx.x}px, ${drag!.deltaPx.y}px)` }
          : {}
        return (
          <div key={o.id} className="absolute inset-0 pointer-events-none" style={style}>
            <OverlayItem
              overlay={o}
              output={output}
              hideEmblem={hasLogo && (o.kind === 'title' || o.kind === 'end')}
            />
          </div>
        )
      })}

      {overlays.map((o) => {
        const box = hitBoxFor(o, output)
        if (!box) return null
        const selected = isSelected(o.id)
        // task spec §2-3: Logo/EndCard は位置固定 — ドラッグ不可、カーソルで示す。
        const notDraggable = o.kind === 'logo' || o.kind === 'end'
        const style: React.CSSProperties = {
          left: `${box.leftNorm * 100}%`,
          top: `${box.topNorm * 100}%`,
          width: `${box.widthNorm * 100}%`,
          height: `${box.heightNorm * 100}%`,
        }
        return (
          <div
            key={`hit-${o.id}`}
            data-testid="overlay-hit"
            data-overlay-kind={o.kind}
            data-overlay-id={o.id}
            title={notDraggable ? '位置は固定です' : undefined}
            className={`absolute rounded pointer-events-auto border-2 ${
              selected ? 'border-accent-blue' : 'border-transparent hover:border-white/25'
            } ${notDraggable ? 'cursor-not-allowed' : o.kind === 'free' ? 'cursor-move' : 'cursor-grab active:cursor-grabbing'}`}
            style={style}
            onClick={(e) => {
              e.stopPropagation()
              // task spec §2-4: プレビュー上のオーバーレイをクリックすると選択される。
              if (e.shiftKey) toggleSelection({ kind: 'overlay', id: o.id })
              else select({ kind: 'overlay', id: o.id })
            }}
            onPointerDown={(e) => {
              if (notDraggable) return // 選択は onClick に任せる。ドラッグは開始しない。
              e.stopPropagation()
              ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
              beginHistoryBatch()
              let next: DragState | null = null
              if (o.kind === 'free') {
                next = {
                  overlayId: o.id,
                  mode: 'free',
                  startClientX: e.clientX,
                  startClientY: e.clientY,
                  startX: o.x,
                  startY: o.y,
                  deltaPx: { x: 0, y: 0 },
                }
              } else if (o.kind === 'title' || o.kind === 'label') {
                next = {
                  overlayId: o.id,
                  mode: 'slot',
                  slotKind: o.kind,
                  startClientX: e.clientX,
                  startClientY: e.clientY,
                  deltaPx: { x: 0, y: 0 },
                  nearestSlot: o.position,
                }
              }
              dragRef.current = next
              setDrag(next)
            }}
            onPointerMove={(e) => {
              // reviewer 差し戻し B1: 読み取りは ref（`dragRef.current`）から。
              // setState の functional updater からは読まない（副作用ゼロだが
              // 一貫性のため他ハンドラと同じ「ref が正本」の形に揃える）。
              const prev = dragRef.current
              if (!prev || prev.overlayId !== o.id) return
              const dxPx = e.clientX - prev.startClientX
              const dyPx = e.clientY - prev.startClientY
              let nearestSlot = prev.nearestSlot
              if (prev.mode === 'slot' && prev.slotKind) {
                const rect = rootRef.current?.getBoundingClientRect()
                if (rect && rect.width > 0 && rect.height > 0) {
                  const xNorm = (e.clientX - rect.left) / rect.width
                  const yNorm = (e.clientY - rect.top) / rect.height
                  nearestSlot =
                    prev.slotKind === 'title' ? nearestTitleSlot({ xNorm, yNorm }) : nearestLabelSlot({ xNorm, yNorm })
                }
              }
              const next: DragState = { ...prev, deltaPx: { x: dxPx, y: dyPx }, nearestSlot }
              dragRef.current = next
              setDrag(next)
            }}
            onPointerUp={(e) => {
              ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
              // reviewer 差し戻し B1: ref を読んでから即クリア（setState は描画の
              // ミラー更新のみ）。updateOverlay/endHistoryBatch は setState の
              // 外側・通常の同期コードとして1回だけ実行する。
              const prev = dragRef.current
              dragRef.current = null
              setDrag(null)
              if (!prev || prev.overlayId !== o.id) return
              const rect = rootRef.current?.getBoundingClientRect()
              if (prev.mode === 'free' && o.kind === 'free' && rect && rect.width > 0 && rect.height > 0) {
                const dxNorm = (e.clientX - prev.startClientX) / rect.width
                const dyNorm = (e.clientY - prev.startClientY) / rect.height
                const resolved = resolveFreeTextPosition(
                  { xNorm: (prev.startX ?? o.x) + dxNorm, yNorm: (prev.startY ?? o.y) + dyNorm },
                  e.shiftKey,
                )
                if (resolved.xNorm !== o.x || resolved.yNorm !== o.y) {
                  updateOverlay(o.id, { x: resolved.xNorm, y: resolved.yNorm })
                }
              } else if (prev.mode === 'slot' && rect && rect.width > 0 && rect.height > 0) {
                const xNorm = (e.clientX - rect.left) / rect.width
                const yNorm = (e.clientY - rect.top) / rect.height
                if (o.kind === 'title') {
                  const slot = nearestTitleSlot({ xNorm, yNorm })
                  if (slot !== o.position) updateOverlay(o.id, { position: slot })
                } else if (o.kind === 'label') {
                  const slot = nearestLabelSlot({ xNorm, yNorm })
                  if (slot !== o.position) updateOverlay(o.id, { position: slot })
                }
              }
              endHistoryBatch()
            }}
            onPointerCancel={(e) => {
              ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
              const prev = dragRef.current
              dragRef.current = null
              setDrag(null)
              if (!prev || prev.overlayId !== o.id) return
              endHistoryBatch()
            }}
          />
        )
      })}

      {drag?.mode === 'slot' && drag.slotKind && <SlotGhosts slotKind={drag.slotKind} nearest={drag.nearestSlot} />}
    </div>
  )
}
